const VERSION = 1;

const KEY_PREFS = "npp:prefs";
const KEY_PREFS_VERSION = "npp:prefs:version";

const KEY_CLIENT_TOKEN = "client_token";
const KEY_SPLIT_GROUP_ID = "split_group_id";
const KEY_RATEAPP_IS_RATE = "rateapp_request__rate";
const KEY_RATEAPP_IS_SKIP = "rateapp_request__skip";
const KEY_RATEAPP_LAST_SHOW = "rateapp_request__last_show";
const KEY_MASK_TOOLTIP_IS_HIDDEN = "mask_tooltip_is_hidden";
const KEY_PRO_WATERMARK_SHOULD_BE_REMOVED = "pro_watermark_should_be_removed";
const KEY_PRO_WATERMARK_SHOW_MODAL_INCREMENT = "pro_watermark_show_modal_increment";
const KEY_FIRST_VISIT_AT = "first_visit_at";
const KEY_FIRST_PROCESSING_PROCESSED_AT = "first_processing_processed_at";
const KEY_FIRST_DOWNLOAD_AT = "first_download_at";
const KEY_SHOW_FULL_SIZE_PROMOTION_COUNTER = "show_full_size_promotion_counter";
const KEY_COUNT_PROCESSING_IN_SESSION = "count_processing_in_session";
const KEY_COUNT_PROCESSING_IN_VARIATION_CONFIG = "count_processing_in_variation_config";
const KEY_PROCESSED_PHOTOS_AMOUNT = "processed_photos_amount";
const KEY_SELECTED_LANG = "selected_lang";
const KEY_SELECTED_PHOTOS_AMOUNT = "selected_photos_amount";
const KEY_D28_TAB_REFRESH = "d28_tab_refresh";
const KEY_D28_TAB_PROCESSED = "d28_tab_processed";
const KEY_SHOW_TOONORG_BANNER = "show_toonorg_banner";
const KEY_PROCESSING_BANNER_COUNTER = "processing_banner_counter";
const PREFIX_PROCESSING_BANNER_ROTATION_INDEX = "processing_banner_rotation_idx_";

class ClientStorage {

  constructor() {
    const prefs = window.localStorage.getItem(KEY_PREFS);
    if (prefs) {
      this.prefs = JSON.parse(prefs);
    } else {
      this.prefs = {};
    }

    const currentVersion = parseInt(window.localStorage.getItem(KEY_PREFS_VERSION) || 1);
    if (currentVersion !== VERSION) {
      this.migrate(currentVersion, VERSION);
      window.localStorage.setItem(KEY_PREFS_VERSION, "" + VERSION);
    }
  }

  migrate(fromVersion, toVersion) {
    // ...
  }

  commit() {
    window.localStorage.setItem(KEY_PREFS, JSON.stringify(this.prefs));
  }

  setBoolean(key, value) {
    this.prefs[key] = !!value;
    this.commit();
  }

  getBoolean(key, defaultValue) {
    if (this.prefs[key] !== undefined) {
      return !!this.prefs[key];
    } else {
      return defaultValue;
    }
  }

  setInteger(key, value) {
    this.prefs[key] = parseInt(value);
    this.commit();
  }

  getInteger(key, defaultValue) {
    if (this.prefs[key] !== undefined) {
      return parseInt(this.prefs[key]);
    } else {
      return defaultValue;
    }
  }

  setString(key, value) {
    this.prefs[key] = "" + value;
    this.commit();
  }

  getString(key, defaultValue) {
    if (this.prefs[key] !== undefined) {
      return "" + this.prefs[key];
    } else {
      return defaultValue;
    }
  }

  // ---

  getClientToken() {
    return this.getString(KEY_CLIENT_TOKEN);
  }

  setClientToken(value) {
    this.setString(KEY_CLIENT_TOKEN, value);
  }

  getFirstVisitAt() {
    return this.getInteger(KEY_FIRST_VISIT_AT);
  }

  setFirstVisitAt(value) {
    this.setInteger(KEY_FIRST_VISIT_AT, value);
  }

  hasFirstVisitAt() {
    return this.getInteger(KEY_FIRST_VISIT_AT, Number.MIN_VALUE) !== Number.MIN_VALUE;
  }

  getFirstProcessingProcessedAt() {
    return this.getInteger(KEY_FIRST_PROCESSING_PROCESSED_AT);
  }

  setFirstProcessingProcessedAt(value) {
    this.setInteger(KEY_FIRST_PROCESSING_PROCESSED_AT, value);
  }

  hasFirstProcessingProcessedAt() {
    return this.getInteger(KEY_FIRST_PROCESSING_PROCESSED_AT, Number.MIN_VALUE) !== Number.MIN_VALUE;
  }

  getSplitGroupId() {
    return this.getInteger(KEY_SPLIT_GROUP_ID, undefined);
  }

  setSplitGroupId(value) {
    this.setInteger(KEY_SPLIT_GROUP_ID, value);
  }

  setRateAppRequestIsRate(value) {
    this.setBoolean(KEY_RATEAPP_IS_RATE, value);
  }

  getRateAppRequestIsRate() {
    return this.getBoolean(KEY_RATEAPP_IS_RATE, false);
  }

  setRateAppRequestIsSkip(value) {
    this.setBoolean(KEY_RATEAPP_IS_SKIP, value);
  }

  getRateAppRequestIsSkip() {
    return this.getBoolean(KEY_RATEAPP_IS_SKIP, false);
  }

  setRateAppRequestLastShow(value) {
    this.setInteger(KEY_RATEAPP_LAST_SHOW, value);
  }

  getRateAppRequestLastShow() {
    return this.getInteger(KEY_RATEAPP_LAST_SHOW, 0);
  }

  setMaskTooltipIsHidden(value) {
    this.setBoolean(KEY_MASK_TOOLTIP_IS_HIDDEN, value);
  }

  getMaskTooltipIsHidden() {
    return this.getBoolean(KEY_MASK_TOOLTIP_IS_HIDDEN, false);
  }

  getProWatermarkShouldBeRemoved() {
    return this.getBoolean(KEY_PRO_WATERMARK_SHOULD_BE_REMOVED, false);
  }

  setProWatermarkShouldBeRemoved(value) {
    this.setBoolean(KEY_PRO_WATERMARK_SHOULD_BE_REMOVED, value);
  }

  getProWatermarkShowModal() {
    return this.getInteger(KEY_PRO_WATERMARK_SHOW_MODAL_INCREMENT, 0);
  }

  incrementProWatermarkShowModal() {
    const value = this.getProWatermarkShowModal() + 1;

    this.setInteger(KEY_PRO_WATERMARK_SHOW_MODAL_INCREMENT, value);
  }

  getFirstDownloadAt() {
    return this.getInteger(KEY_FIRST_DOWNLOAD_AT);
  }

  setFirstDownloadAt(value) {
    this.setInteger(KEY_FIRST_DOWNLOAD_AT, value);
  }

  getShowFullSizePromotionCounter() {
    return this.getInteger(KEY_SHOW_FULL_SIZE_PROMOTION_COUNTER, 0);
  }

  incrementShowFullSizePromotionCounter() {
    const value = this.getShowFullSizePromotionCounter() + 1;

    this.setInteger(KEY_SHOW_FULL_SIZE_PROMOTION_COUNTER, value);
  }

  setCountProcessingInSession(sessionId, count) {
    this.setString(KEY_COUNT_PROCESSING_IN_SESSION, `${sessionId}:${count}`);
  }

  getCountProcessingInSession() {
    return this.getString(KEY_COUNT_PROCESSING_IN_SESSION, "-1:0")
      .split(":")
      .map((item) => parseInt(item));
  }

  setCountProcessingInVariationConfig(rolloutTime, count) {
    this.setString(KEY_COUNT_PROCESSING_IN_VARIATION_CONFIG, `${rolloutTime}:${count}`);
  }

  getCountProcessingInVariationConfig() {
    return this.getString(KEY_COUNT_PROCESSING_IN_VARIATION_CONFIG, "-1:0")
      .split(":")
      .map((item) => parseInt(item));
  }

  getProcessedPhotosAmount() {
    return this.getInteger(KEY_PROCESSED_PHOTOS_AMOUNT, 0);
  }

  incrementProcessedPhotosAmount() {
    this.setInteger(
      KEY_PROCESSED_PHOTOS_AMOUNT,
      this.getInteger(KEY_PROCESSED_PHOTOS_AMOUNT, 0) + 1
    );
  }

  getSelectedLang() {
    return this.getString(KEY_SELECTED_LANG, null);
  }

  setSelectedLang(value) {
    this.setString(KEY_SELECTED_LANG, value);
  }

  getSelectedPhotosAmount() {
    return this.getInteger(KEY_SELECTED_PHOTOS_AMOUNT, 0);
  }

  incrementSelectedPhotosAmount() {
    this.setInteger(KEY_SELECTED_PHOTOS_AMOUNT, this.getSelectedPhotosAmount() + 1);
  }

  getD28TabRefreshAmount() {
    return this.getInteger(KEY_D28_TAB_REFRESH, 0);
  }

  incrementD28TabRefreshAmount() {
    this.setInteger(KEY_D28_TAB_REFRESH, this.getD28TabRefreshAmount() + 1);
  }

  setD28TabIsProcessed() {
    return this.setInteger(KEY_D28_TAB_PROCESSED, Date.now());
  }

  getD28TabIsProcessed() {
    return this.getInteger(KEY_D28_TAB_PROCESSED, null);
  }

  getShouldDisplayToonorgBanner() {
    return this.getBoolean(KEY_SHOW_TOONORG_BANNER, false);
  }

  setShouldDisplayToonorgBanner() {
    this.setBoolean(KEY_SHOW_TOONORG_BANNER, true);
  }

  getProcessingBannerCounter() {
    return this.getInteger(KEY_PROCESSING_BANNER_COUNTER, 0);
  }

  incrementProcessingBannerCounter() {
    this.setInteger(KEY_PROCESSING_BANNER_COUNTER, this.getProcessingBannerCounter() + 1);
  }

  getProcessingBannerRotationIndex(name) {
    return this.getInteger(PREFIX_PROCESSING_BANNER_ROTATION_INDEX + name, 0);
  }

  incrementProcessingBannerRotationIndex(name) {
    this.setInteger(
      PREFIX_PROCESSING_BANNER_ROTATION_INDEX + name,
      this.getProcessingBannerRotationIndex(name) + 1
    );
  }
}

export default new ClientStorage();
